import { encodeURI } from "js-base64";
import isEmpty from "lodash/isEmpty";
import { ReadonlyURLSearchParams } from "next/navigation";
import { kebabCase } from "@portal/app/_common/lib/changeCase";
import QueryString from "query-string";

import type { Role } from "../types/role";
import type { ProviderType } from "../types/provider";
import { providerTypeQueryMap } from "../data/enumValueMaps";
import { getUriFromSlugParts } from "./marketplace/slugHelpers";
import { VOCATION_INDUSTRY, VocationIndustry } from "../types/vocationIndustry";
import { Step } from "./coach";
import { ProgramMetadata } from "@portal/app/_common/types/program";
import { buildCompositeQuerySegments } from "@portal/app/_common/lib/preLeadRedirectComposite";
import {
  DEFAULT_PRE_LEAD_REDIRECT_PARAM_NAMES,
  getReservedPreLeadRedirectParamNames,
} from "@portal/app/_common/lib/preLeadRedirectAffiliateParams";
import merge from "lodash/merge";
import { JobSource } from "../types/typesense/jobs/job";
import { DREAM_SEARCH_LOCATION_PROMPT_PARAM } from "./dreamSearchRoutes";

/**
 * The general principle here is
 * - Define admin-only and shared staff (admin and school) URLs in `adminAndSchoolUrls`.
 * - For school URLs that are different, override them by redefining them in
 *   `schoolUrls`. The signatures must match.
 */
export const adminAndSchoolRootUrl = "/manage";
export const schoolRootUrl = `${adminAndSchoolRootUrl}/school`;
export const customerRootUrl = "";

type QueryObject = Record<
  string,
  | string
  | number
  | boolean
  | readonly string[]
  | readonly number[]
  | readonly boolean[]
  | null
  | undefined
>;
const isSearchParams = (
  query?: QueryObject | ReadonlyURLSearchParams | null
): query is ReadonlyURLSearchParams => query instanceof ReadonlyURLSearchParams;
export const handleOptionalQuery = (query?: QueryObject | ReadonlyURLSearchParams | null) =>
  !query
    ? ""
    : isSearchParams(query)
      ? query?.toString()
        ? `?${query?.toString()}`
        : ""
      : !isEmpty(query)
        ? `?${QueryString.stringify(query)}`
        : "";

/**
 * Urls that apply to any role or no role (unauthenticated)
 * These are available when using getUrls with any role or "GENERAL"
 */
const generalUrls = {
  About: () => `/about`,
  Affiliate: (slug: string, industry: VocationIndustry) => `/page/${slug}/${industry}`,
  Blog: (slug: string) => `/blog/${slug}`,
  BlogHome: () => `/blog`,
  Coach: () => `/coach`,
  CoachQuestionStep: (key: string) => `/coach/question/${kebabCase(key)}`,
  CoachStep: (step: Step) => `/coach/${kebabCase(step)}`,
  ClaimSchool: () => `https://dreambound.com/schools/claim`,
  Explore: () => `/explore`,
  DreamSearch: ({
    industry,
    zip,
    filterQueryParams,
    shouldPromptForLocation = false,
  }: {
    industry?: VocationIndustry;
    zip: string | undefined | null;
    filterQueryParams?: Parameters<typeof handleOptionalQuery>[0];
    shouldPromptForLocation?: boolean;
  }) => {
    const filters = isSearchParams(filterQueryParams)
      ? Object.fromEntries(
          [...filterQueryParams.keys()].map((key) => [key, filterQueryParams.getAll(key)])
        )
      : filterQueryParams;
    const queryParams = industry
      ? { industry: kebabCase(industry), ...filters }
      : filterQueryParams;

    const url = `/explore${zip ? `/${zip}` : ""}${handleOptionalQuery(queryParams)}`;
    if (!shouldPromptForLocation) return url;

    return `${url}${url.includes("?") ? "&" : "?"}${DREAM_SEARCH_LOCATION_PROMPT_PARAM}=true`;
  },
  DreamSearchClick: ({
    programId,
    jornayaLeadId,
    isClickout,
    formFunnelId,
    marketplaceEventId,
  }: {
    programId: string;
    isClickout: boolean;
    jornayaLeadId?: string;
    formFunnelId?: string;
    marketplaceEventId?: string;
  }) => {
    // Note: the Everflow transaction ID is deliberately not included in the
    // URL. The /dream-search-click server action reads the captured attribution
    // cookie (ef_tid_c_o_1) so sharing a downstream URL doesn't copy the sender's ID.
    const params = new URLSearchParams({
      p: programId,
      co: isClickout ? "true" : "false",
      ...(jornayaLeadId && { j: jornayaLeadId }),
      ...(formFunnelId && { ff: formFunnelId }),
      ...(marketplaceEventId && { m: marketplaceEventId }),
    });
    return `/dream-search-click?${params.toString()}`;
  },
  /**
   * Checks if a pathname is a DreamSearch page (either /explore or an industry-specific route like /health)
   */
  DreamSearchMatches: (pathname: string) =>
    pathname.startsWith("/explore") ||
    Object.values(VOCATION_INDUSTRY).some((industry) =>
      pathname.startsWith(`/${kebabCase(industry)}`)
    ),
  DreamWall: () => `https://dreambound.com/dreamwall`,
  Employers: () => `/employers`,
  FederalFinancialAid: () => `https://studentaid.gov/fsa-id/create-account/launch`,
  FormFunnelHome: () => `/flows/home`,
  /** Public form-funnel page for a slug (`/flows/[slug]` route). */
  Flow: (slug: string) => `/flows/${slug}`,
  Home: () => `/`,
  JobClick: ({
    jobId,
    jobSource,
    redirectUrl,
  }: {
    jobId?: string;
    jobSource?: JobSource;
    redirectUrl?: string;
  }) =>
    `/job-click?${[
      jobId && `db_j=${jobId}`,
      jobSource && `db_s=${jobSource}`,
      redirectUrl && `db_url=${redirectUrl}`,
    ]
      .filter(Boolean)
      .join("&")}`,
  Jobs: () => `/company/jobs`,
  LeadRedirect: (redirect: string) => `/outbound?r=${encodeURI(redirect)}`,
  LegalTermsAndConditions: () => `https://dreambound.com/terms-and-conditions`,
  LegalPrivacy: () => `https://dreambound.com/privacy-policy`,
  Login: (redirect?: string) => `/login${redirect ? `?r=${redirect}` : ""}`,
  LoginMagicLink: ({ email, code }: { email: string; code: string }) =>
    `/login?e=${encodeURIComponent(email)}&c=${code}`,
  Metro: (slug: string) => `/view/${slug}`,
  PreLeadRedirect: (
    {
      preLeadRedirect,
      preLeadRedirectParams,
      preLeadRedirectCompositeParams,
      preLeadRedirectAffiliateParams,
      preLeadRedirectSuppressDefaultParams,
    }: {
      preLeadRedirect: string;
      preLeadRedirectParams?: ProgramMetadata["preLeadRedirectParams"] | null;
      preLeadRedirectCompositeParams?: ProgramMetadata["preLeadRedirectCompositeParams"] | null;
      preLeadRedirectAffiliateParams?: ProgramMetadata["preLeadRedirectAffiliateParams"] | null;
      preLeadRedirectSuppressDefaultParams?: boolean | null;
    },
    {
      customerId,
      everflowAffiliateId,
      everflowTransactionId,
      marketplaceEventId,
      programSlug,
      userZip,
    }: {
      customerId?: string;
      /** Everflow affiliate the visitor landed with; selects preLeadRedirectAffiliateParams. */
      everflowAffiliateId?: string;
      everflowTransactionId?: string;
      marketplaceEventId?: string;
      programSlug?: string;
      userZip?: string;
    },
    // Used for previewing in admin portal
    dbIDsOnly = false
  ) => {
    const [baseUrl, query = ""] = preLeadRedirect!.split("?");
    const params = new URLSearchParams(query);
    // When suppressed (e.g. a composite-only partner like UMA), skip the simple
    // per-ID Dreambound params (default m/c/e/s/z and any custom renames) so only
    // the base URL's own query and the composite param(s) are emitted. Composites
    // below are unaffected.
    if (!preLeadRedirectSuppressDefaultParams) {
      const paramNameMap = merge(
        { ...DEFAULT_PRE_LEAD_REDIRECT_PARAM_NAMES },
        preLeadRedirectParams
      );
      if (marketplaceEventId && paramNameMap["marketplaceEventId"]) {
        params.set(paramNameMap["marketplaceEventId"], marketplaceEventId);
      }
      if (customerId && paramNameMap["customerId"]) {
        params.set(paramNameMap["customerId"], customerId);
      }
      if (everflowTransactionId && paramNameMap["everflowTransactionId"]) {
        params.set(paramNameMap["everflowTransactionId"], everflowTransactionId);
      }
      if (programSlug && paramNameMap["programSlug"]) {
        params.set(paramNameMap["programSlug"], programSlug);
      }
      if (userZip && paramNameMap["userZip"]) {
        params.set(paramNameMap["userZip"], userZip);
      }
    }
    // Static params keyed by the visitor's Everflow affiliate (e.g. PEC's
    // subsrc1/subsrc2/webinit per traffic source) override the base URL's
    // defaults for a matching affiliate; an unmapped or missing affiliate
    // leaves the base URL as-is. Explicit per-partner config, so it applies
    // even when the default ID params are suppressed. A mapped name can never
    // replace an ID or composite param: rejected on save, and skipped here for
    // config written outside the admin form.
    const affiliateParams = everflowAffiliateId
      ? preLeadRedirectAffiliateParams?.[everflowAffiliateId]
      : undefined;
    if (affiliateParams) {
      const reserved = getReservedPreLeadRedirectParamNames({
        preLeadRedirectParams,
        preLeadRedirectCompositeParams,
      });
      for (const [name, value] of Object.entries(affiliateParams)) {
        if (!reserved.has(name)) params.set(name, value);
      }
    }
    let dbIDParams = handleOptionalQuery(Object.fromEntries(params.entries()));

    // Composite params (e.g. UMA's `vendorleadid`) bundle several IDs into a
    // single param with an exact, partner-specified encoding. They must bypass
    // query-string serialization (which would turn ":" into "%3A" and
    // double-encode the "%" in "%7C"), so we assemble them ourselves and append
    // them to the query verbatim. The whole string is base64url-wrapped below,
    // so what we append is exactly what the partner receives.
    const compositeSegments = buildCompositeQuerySegments(preLeadRedirectCompositeParams, {
      marketplaceEventId,
      customerId,
      everflowTransactionId,
      programSlug,
      userZip,
    });
    if (compositeSegments.length) {
      dbIDParams = `${dbIDParams}${dbIDParams ? "&" : "?"}${compositeSegments.join("&")}`;
    }

    return dbIDsOnly ? dbIDParams : `/outbound?r=${encodeURI(`${baseUrl}${dbIDParams}`)}`;
  },
  ProviderProgramStorefront: (
    providerSlug: string,
    programSlug: string,
    query?: { autoOpen: true }
  ) => `${getUriFromSlugParts("school", providerSlug, programSlug)}${handleOptionalQuery(query)}`,
  ProviderStorefront: (providerSlug: string) => getUriFromSlugParts("school", providerSlug),
  Schools: () => `/schools`,
};

/**
 * URLs that apply to both schools and admins, or only admins.
 * These are available when using getUrls with role "SCHOOL" or "ADMIN".
 */
const adminAndSchoolUrls = {
  AdminTools: () => `${adminAndSchoolRootUrl}/admin_tools`,
  AdminToolsAlerts: () => `${adminAndSchoolRootUrl}/admin_tools/alerts`,
  AdminToolsActions: () => `${adminAndSchoolRootUrl}/admin_tools/actions`,
  AdminToolsAirtableSync: () => `${adminAndSchoolRootUrl}/admin_tools/sync_debug`,
  AdminToolsDebug: () => `${adminAndSchoolRootUrl}/admin_tools/debug`,
  AdminToolsForcedError: () => `${adminAndSchoolRootUrl}/admin_tools/forced_error`,
  AdminToolsImageUpload: () => `${adminAndSchoolRootUrl}/admin_tools/image_upload`,
  AdminToolsPersonalInfoRemoval: () => `${adminAndSchoolRootUrl}/admin_tools/personal_info_removal`,
  AdminToolsPollOfTheDay: () => `${adminAndSchoolRootUrl}/admin_tools/potd`,
  AdminToolsPollOfTheDayDetails: (pollId: string) =>
    `${adminAndSchoolRootUrl}/admin_tools/potd/${pollId}`,
  AdminToolsQuestionOfTheDay: () => `${adminAndSchoolRootUrl}/admin_tools/qotd`,
  AdminToolsQuestionOfTheDayDetails: (questionId: string) =>
    `${adminAndSchoolRootUrl}/admin_tools/qotd/${questionId}`,
  ApiKeys: () => `${adminAndSchoolRootUrl}/admin_tools/api-keys`,
  AuditEventMatches: () => `${adminAndSchoolRootUrl}/audit`,
  AuditEventView: (auditEventId: string) => `${adminAndSchoolRootUrl}/audit/${auditEventId}`,
  AuditEventList: () => `${adminAndSchoolRootUrl}/audit/list`,
  CustomerMatches: () => `${adminAndSchoolRootUrl}/customer`,
  CustomerList: () => `${adminAndSchoolRootUrl}/customer/list`,
  CustomerView: (customerId: string) => `${adminAndSchoolRootUrl}/customer/${customerId}`,
  CustomerViewEnrollments: (customerId: string) =>
    `${adminAndSchoolRootUrl}/customer/${customerId}/enrollments`,
  CustomerViewInvoicing: (customerId: string) =>
    `${adminAndSchoolRootUrl}/customer/${customerId}/invoicing`,
  CustomerViewActivity: (customerId: string) =>
    `${adminAndSchoolRootUrl}/customer/${customerId}/activity`,
  CustomerViewFormFunnels: (customerId: string) =>
    `${adminAndSchoolRootUrl}/customer/${customerId}/form_funnels`,
  CustomerRefund: (customerId: string) => `${adminAndSchoolRootUrl}/customer/${customerId}/refund`,
  CustomerAdd: () => `${adminAndSchoolRootUrl}/customer/add_customer`,
  CustomerEdit: (customerId: string) =>
    `${adminAndSchoolRootUrl}/customer/${customerId}/edit_customer`,
  CustomerConnectProgramView: (
    customerId: string,
    query?: { type?: ProviderType; enrollmentItemId: string }
  ) =>
    `${adminAndSchoolRootUrl}/customer/${customerId}/connect_program${handleOptionalQuery(query)}`,
  CustomerConnectProgram: (
    customerId: string,
    programId: string,
    query?: { type?: ProviderType; enrollmentItemId: string }
  ) =>
    `${adminAndSchoolRootUrl}/customer/${customerId}/connect_program/${programId}${handleOptionalQuery(
      query
    )}`,
  CustomerEnrollmentsList: (customerId: string) =>
    `${adminAndSchoolRootUrl}/customer/${customerId}/enrollment/list`,
  CustomerPaymentHistoryView: (customerId: string) =>
    `${adminAndSchoolRootUrl}/customer/${customerId}/payment_history`,
  CustomerCreateEnrollmentItemView: (customerId: string) =>
    `${adminAndSchoolRootUrl}/customer/${customerId}/create_enrollment_item`,
  Dashboard: () => `${adminAndSchoolRootUrl}/dashboard`,
  EnrollmentMatches: () => `${adminAndSchoolRootUrl}/enrollment`,
  EnrollmentList: () => `${adminAndSchoolRootUrl}/enrollment/list`,
  EnrollmentItemView: (enrollmentItemId: string) =>
    `${adminAndSchoolRootUrl}/enrollment_item/${enrollmentItemId}`,
  EnrollmentAdjustInvoice: (enrollmentItemId: string) =>
    `${adminAndSchoolRootUrl}/enrollment/${enrollmentItemId}/create_invoice_adjustment`,
  EnrollmentLogPayment: (enrollmentItemId: string) =>
    `${adminAndSchoolRootUrl}/enrollment/${enrollmentItemId}/create_logged_payment`,
  InboundAffiliateAdd: () => `${adminAndSchoolRootUrl}/affiliate/add_inbound_affiliate`,
  InboundAffiliateEdit: (inboundAffiliateId: string) =>
    `${adminAndSchoolRootUrl}/affiliate/${inboundAffiliateId}/edit_inbound_affiliate`,
  InboundAffiliateList: () => `${adminAndSchoolRootUrl}/affiliate/list`,
  InboundAffiliateMatches: () => `${adminAndSchoolRootUrl}/affiliate`,
  LandingPageList: () => `${adminAndSchoolRootUrl}/landing_page/list`,
  LandingPageView: (landingPageId: string) =>
    `${adminAndSchoolRootUrl}/landing_page/${landingPageId}`,
  LandingPageAdd: () => `${adminAndSchoolRootUrl}/landing_page/add_landing_page`,
  LandingPageEdit: (landingPageId: string) =>
    `${adminAndSchoolRootUrl}/landing_page/${landingPageId}/edit_landing_page`,
  LandingPageMatches: () => `${adminAndSchoolRootUrl}/landing_page`,
  NetworkView: (networkId: string) => `${adminAndSchoolRootUrl}/network/${networkId}`,
  NetworkAdd: () => `${adminAndSchoolRootUrl}/network/add_network`,
  NetworkEdit: (networkId: string) => `${adminAndSchoolRootUrl}/network/${networkId}/edit_network`,
  NetworkList: () => `${adminAndSchoolRootUrl}/network/list`,
  NetworkMatches: () => `${adminAndSchoolRootUrl}/network`,
  NotificationsList: () => `${adminAndSchoolRootUrl}/notifications/list`,
  // Outcomes dashboard (admin-only — gated at sidebar + page level)
  OutcomesMatches: () => `${adminAndSchoolRootUrl}/outcomes`,
  OutcomesList: () => `${adminAndSchoolRootUrl}/outcomes`,
  OutcomesIssuesList: () => `${adminAndSchoolRootUrl}/outcomes/issues`,
  OutcomesIssueDetail: (outcomeReviewEventId: string) =>
    `${adminAndSchoolRootUrl}/outcomes/issues/${outcomeReviewEventId}`,
  OutcomesAnalytics: () => `${adminAndSchoolRootUrl}/outcomes/analytics`,
  OutcomesImport: () => `${adminAndSchoolRootUrl}/outcomes/import`,
  OutcomesStatusMapping: () => `${adminAndSchoolRootUrl}/outcomes/status-mapping`,
  OutcomesSnapshotDetail: (snapshotId: string) =>
    `${adminAndSchoolRootUrl}/outcomes/snapshots/${snapshotId}`,
  // Outcomes V2 sections; an optional tab is the route segment after the page.
  OutcomesBoard: () => `${adminAndSchoolRootUrl}/outcomes`,
  OutcomesProvider: (providerId: string, tab?: string) =>
    `${adminAndSchoolRootUrl}/outcomes/providers/${providerId}${tab ? `/${tab}` : ""}`,
  OutcomesFeeds: () => `${adminAndSchoolRootUrl}/outcomes/sources`,
  OutcomesFeed: (sourceSystem: string, tab?: string) =>
    `${adminAndSchoolRootUrl}/outcomes/sources/${sourceSystem}${tab ? `/${tab}` : ""}`,
  OutcomesChange: (versionId: string) => `${adminAndSchoolRootUrl}/outcomes/changes/${versionId}`,
  OutcomesUpload: () => `${adminAndSchoolRootUrl}/outcomes/upload`,
  OutcomesIntake: (tab?: string) =>
    `${adminAndSchoolRootUrl}/outcomes/intake${tab ? `/${tab}` : ""}`,
  OutcomesJobs: () => `${adminAndSchoolRootUrl}/outcomes/jobs`,
  OutcomesJob: (jobId: string) => `${adminAndSchoolRootUrl}/outcomes/jobs/${jobId}`,
  OutcomesLeads: () => `${adminAndSchoolRootUrl}/outcomes/leads`,
  PayerMatches: () => `${adminAndSchoolRootUrl}/payer`,
  PayerList: () => `${adminAndSchoolRootUrl}/payer/list`,
  PayerAdjustInvoice: (invoicePayerId: string) =>
    `${adminAndSchoolRootUrl}/payer/${invoicePayerId}/create_invoice_adjustment`,
  PayerBalanceTransfer: (invoicePayerId: string) =>
    `${adminAndSchoolRootUrl}/payer/${invoicePayerId}/create_balance_transfer`,
  PayerRefreshView: (invoicePayerId: string) =>
    `${adminAndSchoolRootUrl}/payer/${invoicePayerId}/refresh`,
  PayerView: (invoicePayerId: string) => `${adminAndSchoolRootUrl}/payer/${invoicePayerId}`,
  CampaignList: () => `${adminAndSchoolRootUrl}/campaign/list`,
  CampaignAdd: () => `${adminAndSchoolRootUrl}/campaign/add_campaign`,
  CampaignEdit: (campaignId: string) => `${adminAndSchoolRootUrl}/campaign/${campaignId}/edit`,
  CampaignView: (campaignId: string) => `${adminAndSchoolRootUrl}/campaign/${campaignId}`,
  // Prefix, not a page: the sidebar highlights on every campaign route, so a
  // detail page added later lights the same entry without another change here.
  CampaignMatches: () => `${adminAndSchoolRootUrl}/campaign`,
  // Under `/campaign` because that is what it is: the same campaigns, scoped to
  // the months ahead. It has no sidebar entry of its own — it is reached from
  // the campaign list — so `CampaignMatches` lighting up here is correct rather
  // than a second highlight competing with it.
  UpcomingMonth: () => `${adminAndSchoolRootUrl}/campaign/upcoming_month`,
  // Its own route rather than one under `/campaign`: a hold names a network,
  // provider, program or campaign, and only the last belongs to that section.
  PauseList: () => `${adminAndSchoolRootUrl}/pause/list`,
  PauseMatches: () => `${adminAndSchoolRootUrl}/pause`,
  // Predicted Start Rate settings: the global page, and the per-school tab.
  LeadScoringSettings: () => `${adminAndSchoolRootUrl}/lead_scoring`,
  ProviderLeadScoring: (providerId: string) =>
    `${adminAndSchoolRootUrl}/provider/${providerId}/lead_scoring`,
  ProviderOutcomes: (providerId: string) =>
    `${adminAndSchoolRootUrl}/provider/${providerId}/outcomes`,
  ProgramMatches: () => `${adminAndSchoolRootUrl}/program`,
  ProgramList: () => `${adminAndSchoolRootUrl}/program/list`,
  ProgramSettings: (programId: string) => `${adminAndSchoolRootUrl}/program/${programId}/settings`,
  ProviderAvailability: (providerId: string) =>
    `${adminAndSchoolRootUrl}/provider/${providerId}/availability`,
  ProgramAvailability: (programId: string) =>
    `${adminAndSchoolRootUrl}/program/${programId}/availability`,
  ProgramRecentActivity: (programId: string) =>
    `${adminAndSchoolRootUrl}/program/${programId}/recent_activity`,
  ProgramEnrollments: (programId: string) =>
    `${adminAndSchoolRootUrl}/program/${programId}/enrollments`,
  ProgramEdit: (programId: string) => `${adminAndSchoolRootUrl}/program/${programId}/edit_program`,
  ProgramCostEdit: (programId: string) =>
    `${adminAndSchoolRootUrl}/program/${programId}/program_cost/update_program_costs`,
  ProgramCostList: (programId: string) =>
    `${adminAndSchoolRootUrl}/program/${programId}/program_cost/list`,
  ProgramPaymentOptionView: (programId: string, paymentOptionId: string) =>
    `${adminAndSchoolRootUrl}/program/${programId}/payment_option/${paymentOptionId}`,
  ProgramPaymentOptionEdit: (programId: string, paymentOptionId: string) =>
    `${adminAndSchoolRootUrl}/program/${programId}/payment_option/${paymentOptionId}/edit`,
  ProgramPaymentOptionList: (programId: string) =>
    `${adminAndSchoolRootUrl}/program/${programId}/payment_option/list`,
  ProgramPaymentOptionAdd: (programId: string) =>
    `${adminAndSchoolRootUrl}/program/${programId}/payment_option/add`,
  ProgramRevenueConfigEdit: (programId: string) =>
    `${adminAndSchoolRootUrl}/program/${programId}/edit_revenue_configuration`,
  ProgramReviewAdd: (programId: string) =>
    `${adminAndSchoolRootUrl}/program/${programId}/add_review`,
  ProgramReviewEdit: (programId: string, reviewId: string) =>
    `${adminAndSchoolRootUrl}/program/${programId}/reviews/${reviewId}/edit_review`,
  ProgramReviewList: (programId: string) =>
    `${adminAndSchoolRootUrl}/program/${programId}/reviews/list`,
  ProviderMatches: () => `${adminAndSchoolRootUrl}/provider`,
  ProviderList: () => `${adminAndSchoolRootUrl}/provider/schools`,
  ProviderSchoolsByLocationList: () => `${adminAndSchoolRootUrl}/provider/schools_by_location`,
  ProviderSponsorList: () => `${adminAndSchoolRootUrl}/provider/sponsors`,
  ProviderEmployerList: () => `${adminAndSchoolRootUrl}/provider/employers`,
  ProviderView: (providerId: string) => `${adminAndSchoolRootUrl}/provider/${providerId}`,
  ProviderReviewAdd: (providerId: string) =>
    `${adminAndSchoolRootUrl}/provider/${providerId}/add_review`,
  ProviderReviewEdit: (providerId: string, reviewId: string) =>
    `${adminAndSchoolRootUrl}/provider/${providerId}/reviews/${reviewId}/edit_review`,
  ProviderReviewList: (providerId: string) =>
    `${adminAndSchoolRootUrl}/provider/${providerId}/reviews/list`,
  ProgramAddProvider: () => `${adminAndSchoolRootUrl}/provider/add_provider`,
  ProgramAddEmployer: () => `${adminAndSchoolRootUrl}/provider/add_employer`,
  ProviderEdit: (providerId: string) =>
    `${adminAndSchoolRootUrl}/provider/${providerId}/edit_provider`,
  ProviderAddProgram: (providerId: string, providerType?: ProviderType) =>
    `${adminAndSchoolRootUrl}/provider/${providerId}/add_program${handleOptionalQuery({
      ...(providerType && { flow: providerTypeQueryMap[providerType] }),
    })}`,
  ProviderAddSponsorView: () => `${adminAndSchoolRootUrl}/provider/add_sponsor`,
  ProviderLeadDeliveryMappings: (providerId: string) =>
    `${adminAndSchoolRootUrl}/provider/${providerId}/lead_delivery_mappings`,
  ProviderProgramList: (providerId: string) =>
    `${adminAndSchoolRootUrl}/provider/${providerId}/programs`,
  ProviderUpdateNetwork: (providerId: string) =>
    `${adminAndSchoolRootUrl}/provider/${providerId}/add_to_network`,
  ProviderUserList: (providerId: string) =>
    `${adminAndSchoolRootUrl}/provider/${providerId}/user/list`,
  SchoolProgramAdd: (providerId: string) =>
    `${adminAndSchoolRootUrl}/provider/${providerId}/add_program`,
  UserEdit: (userId: string) => `${adminAndSchoolRootUrl}/user/${userId}/edit`,
  UserMatches: () => `${adminAndSchoolRootUrl}/user`,
  UserList: () => `${adminAndSchoolRootUrl}/user/list`,
  VocationList: () => `${adminAndSchoolRootUrl}/vocation/list`,
  VocationMatches: () => `${adminAndSchoolRootUrl}/vocation`,
  VocationEdit: (vocationId: string) =>
    `${adminAndSchoolRootUrl}/vocation/${vocationId}/edit_vocation`,
  VocationAdd: () => `${adminAndSchoolRootUrl}/vocation/add_vocation`,
  VocationView: (vocationId: string) => `${adminAndSchoolRootUrl}/vocation/${vocationId}`,

  // Form Funnel Enrollments
  FormFunnelEnrollmentList: () => `${adminAndSchoolRootUrl}/form_funnel_enrollment/list`,
  FormFunnelEnrollmentMatches: () => `${adminAndSchoolRootUrl}/form_funnel_enrollment`,

  // CMS routes (Payload CMS admin)
  Cms: () => `/cms`,
  CmsFormFunnel: (formFunnelId: string) =>
    `/cms/collections/form-funnels/${encodeURIComponent(formFunnelId)}`,
  CmsMatches: () => `/cms`,
};

/**
 * URLs that apply to both schools and admins, or only schools.
 * These are available when using getUrls with role "SCHOOL".
 */
const schoolUrls: typeof adminAndSchoolUrls = {
  ...adminAndSchoolUrls,
  EnrollmentList: () => `${schoolRootUrl}/enrollment/list`,
  EnrollmentItemView: (enrollmentItemId: string) =>
    `${schoolRootUrl}/enrollment/${enrollmentItemId}`,
  PayerRefreshView: (invoicePayerId: string) => `${schoolRootUrl}/payer/${invoicePayerId}/refresh`,
  PayerView: (invoicePayerId: string) => `${schoolRootUrl}/payer/${invoicePayerId}`,
  ProgramSettings: (programId: string) => `${schoolRootUrl}/program/${programId}/settings`,
  ProgramList: () => `${schoolRootUrl}/program/list`,
  ProgramEdit: (programId: string) => `${schoolRootUrl}/program/${programId}/edit_program`,
  ProgramCostEdit: (programId: string) =>
    `${schoolRootUrl}/program/${programId}/program_cost/update_program_costs`,
  ProgramCostList: (programId: string) => `${schoolRootUrl}/program/${programId}/program_cost/list`,
  ProgramPaymentOptionView: (programId: string, paymentOptionId: string) =>
    `${schoolRootUrl}/program/${programId}/payment_option/${paymentOptionId}`,
  ProgramPaymentOptionList: (programId: string) =>
    `${schoolRootUrl}/program/${programId}/payment_option/list`,
  ProgramPaymentOptionAdd: (programId: string) =>
    `${schoolRootUrl}/program/${programId}/payment_option/add`,
  SchoolProgramAdd: (providerId: string) => `${schoolRootUrl}/provider/${providerId}/add_program`,
  ProviderView: (providerId: string) => `${schoolRootUrl}/provider/${providerId}`,
};

/**
 * URLs that apply only to customers.
 * These are available when using getUrls with role "CUSTOMER".
 */
const customerUrls = {
  Dashboard: () => `${customerRootUrl}/dash`,
  DashboardCoach: () => `${customerRootUrl}/dash/coach`,
  DashboardDreamWall: () => `${customerRootUrl}/dash/dreamwall`,
  DashboardQotd: () => `${customerRootUrl}/dash/qotd`,
  DashboardCoachQuestionnaire: () => `${customerRootUrl}/dash/coach/questionnaire`,
  DashboardCommunity: () => `${customerRootUrl}/dash/community`,
  DashboardResources: () => `${customerRootUrl}/dash/resources`,
  DashboardSupport: () => `${customerRootUrl}/dash/support`,
  // JOBS BOARD: Soft-sunset-- keeping code around for now
  // DashboardJobs: (args?: { jobTitle?: string }) => {
  //   if (!args?.jobTitle) {
  //     return `${customerRootUrl}/dash/jobs`;
  //   }
  //   const params = new URLSearchParams({ q: args.jobTitle });
  //   return `${customerRootUrl}/dash/jobs?${params.toString()}`;
  // },
  DashboardJob: (jobId: string) => `${customerRootUrl}/dash/jobs/${jobId}`,
  DashboardSkipperType: () => `${customerRootUrl}/dash/skipper-type`,
  Favorites: () => `${customerRootUrl}/account/favorites`,
  FavoritesMatches: () => `${customerRootUrl}/account/favorites`,
  ManageAccount: () => `${customerRootUrl}/account/manage`,
  ManageAccountMatches: () => `${customerRootUrl}/account/manage`,
};

export type Urls<R extends Role | "GENERAL"> = {
  ADMIN: typeof adminAndSchoolUrls & typeof generalUrls;
  SCHOOL: typeof schoolUrls & typeof generalUrls;
  CUSTOMER: typeof customerUrls & typeof generalUrls;
  GENERAL: typeof generalUrls;
}[R];

/**
 * Returns URLs contextual to the provided role. Only use when a component
 * may need to access either staff or customer URLs, otherwise use getStaffUrls
 * or getCustomerUrls
 */
export const getUrls = <R extends Role>(role?: R): Urls<R> =>
  ({
    ADMIN: {
      ...adminAndSchoolUrls,
      ...generalUrls,
    },
    SCHOOL: {
      ...schoolUrls,
      ...generalUrls,
    },
    CUSTOMER: {
      ...customerUrls,
      ...generalUrls,
    },
    GENERAL: generalUrls,
  })[(role || "GENERAL") as R];

export const getUrlWithOrigin = (path: string) =>
  `${process.env.DREAM_PORTAL_URL ?? "http://localhost:3000"}${
    path.startsWith("/") ? path : `/${path}`
  }`;

/**
 * Canonical `${origin}/flows/${slug}` landing URL, used as PEC's pre-ping
 * WebLandingURL. Query-param-free (stable canonical page); strips a trailing
 * slash on the origin so we never emit a doubled `//flows/...`.
 */
export const getFormFunnelLandingUrl = (slug: string) => {
  const origin = (process.env.DREAM_PORTAL_URL ?? "http://localhost:3000").replace(/\/+$/, "");
  return `${origin}${generalUrls.Flow(slug)}`;
};

/**
 * Canonical `${origin}/school/${providerSlug}/${programSlug}` program page URL,
 * used as the program-flow pre-ping WebLandingURL (the program flow's analog of
 * `getFormFunnelLandingUrl`). Uses the program-specific page — not the provider
 * storefront — so vendor configs that bind dedup/attribution to the landing
 * page can tell which program produced the lead. Query-param-free; strips a
 * trailing slash on the origin so we never emit a doubled `//school/...`.
 */
export const getProgramStorefrontLandingUrl = (providerSlug: string, programSlug: string) => {
  const origin = (process.env.DREAM_PORTAL_URL ?? "http://localhost:3000").replace(/\/+$/, "");
  return `${origin}${generalUrls.ProviderProgramStorefront(providerSlug, programSlug)}`;
};

export const externalUrls = {
  CareerTrainingNearMe: "https://dreambound.com/career-training-near-me",
  DoNotSellMyInfo: "https://dreambound.com/opt-out/sale-sharing-of-personal-information",
  Facebook: "https://www.facebook.com/withdreambound",
  MailToEmail: "mailto:support@dreambound.com",
  TrustPilot: "https://www.trustpilot.com/review/dreambound.com",
  Twitter: "https://twitter.com/dreambound",
};
